/**
 * 全局分享邀请码兜底捕获（v2.0.148）
 *
 * 背景：分享落地仅 /login 页从 URL 读取 ?invite。若用户分享的是任意业务页
 * （如 /account/define?invite=U-XXXX），未登录访客会被 ProtectedRoute 重定向到
 * /login，原 URL 的 invite 参数随之丢失，无法归因到分享人。
 *
 * 方案：应用启动时把 URL 里的 invite（含 utm / 当前页 redirect）持久化到
 * localStorage，登录页读取时优先 URL、其次 localStorage 兜底，确保邀请关系不丢。
 * v2.2.40：海报生成器二维码用的是 ?ref=CODE 落地首页，与 ?invite= 等价捕获。
 */

const KEY = 'ibiz_pending_invite_v1';
// v2.2.73：邀请兜底数据 7 天有效期。此前无 TTL 且从不清除，用户一旦打开过
// 带 ?invite/?ref 的分享链接（如 /profit-quiz?invite=U-XXX），redirect 就永久
// 驻留 localStorage——之后每次登录都被拽回该页（2026-10-18 用户实测踩坑）。
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingInvite {
  code: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** 分享时所在页面路径（注册成功后回跳到此页，让"分享当前页面"名副其实） */
  redirect?: string;
}

function readUtm(params: URLSearchParams): Pick<PendingInvite, 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'> {
  const u = (k: string) => params.get(k) || undefined;
  return {
    utmSource: u('utm_source'),
    utmMedium: u('utm_medium'),
    utmCampaign: u('utm_campaign'),
    utmTerm: u('utm_term'),
    utmContent: u('utm_content'),
  };
}

/** 仅在"被分享的业务页"上记录回跳目标，认证页（/login 等）本身不回跳自己 */
function sharedRedirect(params: URLSearchParams): string | undefined {
  if (window.location.pathname === '/login' || window.location.pathname === '/tenant-select') {
    return undefined;
  }
  params.delete('invite');
  params.delete('ref');
  const rest = params.toString();
  return window.location.pathname + (rest ? `?${rest}` : '');
}

/**
 * 应用挂载时调用：把 URL 中的 ?invite 落盘到 localStorage。
 * 即使随后被重定向到 /login，邀请关系也不会丢。
 */
export function captureInviteFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    // ?invite=（分享链接）与 ?ref=（海报二维码落地页）等价，均归因到邀请码
    const code = (params.get('invite') || params.get('ref') || '').trim().toUpperCase();
    if (!code) return;
    const data: PendingInvite = { code, ...readUtm(params) };
    // 记录当前页（去掉 invite 本身），供注册后回跳
    data.redirect = sharedRedirect(params);
    // v2.2.73：落盘时间戳，供读取时判定有效期
    localStorage.setItem(KEY, JSON.stringify({ ...data, ts: Date.now() }));
  } catch {
    /* 非致命：localStorage 不可用时不阻塞 */
  }
}

/**
 * 登录页读取：优先 URL（兼容 /login?invite=... 直达），其次 localStorage 兜底
 * （兼容从其他业务页重定向而来、invite 参数已丢失的场景）。
 */
export function getPendingInvite(): PendingInvite | null {
  try {
    const params = new URLSearchParams(window.location.search);
    // ?invite= 与 ?ref=（海报二维码）等价，URL 直达优先
    const urlCode = (params.get('invite') || params.get('ref') || '').trim().toUpperCase();
    if (urlCode) {
      const data: PendingInvite = { code: urlCode, ...readUtm(params) };
      data.redirect = sharedRedirect(params);
      return data;
    }
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PendingInvite & { ts?: number };
      // v2.2.73：无时间戳的历史数据（TTL 引入前落盘的）一律视为过期，
      // 让存量被污染用户无需任何操作即自动痊愈
      if (!parsed.ts || Date.now() - parsed.ts > TTL_MS) {
        localStorage.removeItem(KEY);
        return null;
      }
      return parsed;
    }
  } catch {
    /* 非致命 */
  }
  return null;
}

/** 注册成功后清除兜底数据（避免下次打开登录页误带旧邀请码） */
export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 非致命 */
  }
}
